import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';

// Module-level variable to track if we've checked for reload on this page load
// This resets on actual page reload (module reloads) but persists across React Router navigations
let hasCheckedReload = false;

// Helper function to get initial dismissed state synchronously
const getInitialDismissedState = () => {
  const initKey = 'devBannerInitialized';
  
  // Check if this is a reload (only on first check)
  if (!hasCheckedReload) {
    const navigationEntry = performance.getEntriesByType('navigation')[0];
    const isReload = navigationEntry?.type === 'reload' || 
                     (performance.navigation && performance.navigation.type === 1);
    
    if (isReload) {
      // Page reload - clear flags and show banner
      sessionStorage.removeItem('devBannerDismissed');
      sessionStorage.removeItem(initKey);
      hasCheckedReload = true;
      return false; // Show banner
    }
    hasCheckedReload = true;
  }
  
  // Check if already initialized (React Router navigation)
  const alreadyInitialized = sessionStorage.getItem(initKey);
  if (alreadyInitialized) {
    // Already initialized - respect dismissal state
    const dismissed = sessionStorage.getItem('devBannerDismissed');
    return dismissed === 'true';
  }
  
  // First mount - check dismissal state and mark as initialized
  const dismissed = sessionStorage.getItem('devBannerDismissed');
  sessionStorage.setItem(initKey, 'true');
  return dismissed === 'true';
};

const DevelopmentBanner = ({ isDarkMode }) => {
  // Initialize state synchronously from sessionStorage to prevent flash
  const [isDismissed, setIsDismissed] = useState(() => getInitialDismissedState());

  useEffect(() => {
    const initKey = 'devBannerInitialized';
    const now = Date.now();
    
    // Only check for reload on first mount (module-level check)
    let isReload = false;
    if (!hasCheckedReload) {
      // First time checking - detect if this is a page reload
      const navigationEntry = performance.getEntriesByType('navigation')[0];
      isReload = navigationEntry?.type === 'reload' || 
                 (performance.navigation && performance.navigation.type === 1);
      hasCheckedReload = true;
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/2bfe5830-a8b0-4685-ac6d-05a21d4e13f4',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DevelopmentBanner.jsx:42',message:'First check - detecting reload',data:{isReload,navigationEntryType:navigationEntry?.type,legacyNavType:performance.navigation?.type},timestamp:now,sessionId:'debug-session',runId:'post-fix-v4',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
    } else {
      // Already checked - this is a React Router navigation remount
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/2bfe5830-a8b0-4685-ac6d-05a21d4e13f4',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DevelopmentBanner.jsx:49',message:'Already checked - React Router navigation',data:{alreadyInitialized:sessionStorage.getItem(initKey),sessionStorageValue:sessionStorage.getItem('devBannerDismissed'),currentDismissedState:isDismissed},timestamp:now,sessionId:'debug-session',runId:'post-fix-v4',hypothesisId:'B'})}).catch(()=>{});
      // #endregion
    }
    
    if (isReload) {
      // Page reload - clear everything and show banner
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/2bfe5830-a8b0-4685-ac6d-05a21d4e13f4',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DevelopmentBanner.jsx:54',message:'Page reload detected - clearing all flags',data:{beforeClearDismissed:sessionStorage.getItem('devBannerDismissed'),beforeClearInit:sessionStorage.getItem(initKey)},timestamp:now,sessionId:'debug-session',runId:'post-fix-v4',hypothesisId:'C'})}).catch(()=>{});
      // #endregion
      sessionStorage.removeItem('devBannerDismissed');
      sessionStorage.removeItem(initKey);
      setIsDismissed(false);
    } else {
      // React Router navigation - ensure state matches sessionStorage
      const dismissed = sessionStorage.getItem('devBannerDismissed');
      const shouldBeDismissed = dismissed === 'true';
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/2bfe5830-a8b0-4685-ac6d-05a21d4e13f4',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DevelopmentBanner.jsx:62',message:'React Router navigation - syncing state',data:{dismissedValue:dismissed,shouldBeDismissed,currentDismissedState:isDismissed,needsUpdate:shouldBeDismissed !== isDismissed},timestamp:now,sessionId:'debug-session',runId:'post-fix-v4',hypothesisId:'D'})}).catch(()=>{});
      // #endregion
      if (shouldBeDismissed !== isDismissed) {
        setIsDismissed(shouldBeDismissed);
      }
    }
  }, [isDismissed]);

  const handleDismiss = () => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/2bfe5830-a8b0-4685-ac6d-05a21d4e13f4',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DevelopmentBanner.jsx:56',message:'User dismissed banner',data:{beforeSet:sessionStorage.getItem('devBannerDismissed')},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'E'})}).catch(()=>{});
    // #endregion
    sessionStorage.setItem('devBannerDismissed', 'true');
    setIsDismissed(true);
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/2bfe5830-a8b0-4685-ac6d-05a21d4e13f4',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DevelopmentBanner.jsx:60',message:'Banner dismissed - sessionStorage set',data:{afterSet:sessionStorage.getItem('devBannerDismissed')},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'E'})}).catch(()=>{});
    // #endregion
  };

  if (isDismissed) {
    return null;
  }

  return (
    <div
      className={`w-full ${
        isDarkMode ? 'bg-yellow-900/20 border-b border-yellow-800/30' : 'bg-yellow-50 border-b border-yellow-200'
      } relative z-40 transition-all duration-200`}
    >
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <AlertTriangle
              className={`flex-shrink-0 w-5 h-5 ${
                isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
              }`}
            />
            <p
              className={`text-sm font-medium ${
                isDarkMode ? 'text-yellow-100' : 'text-gray-800'
              }`}
            >
              CivicDuty is in active development. Features are being added regularly. Thank you for your patience!
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className={`ml-4 flex-shrink-0 p-1 rounded-md transition-colors ${
              isDarkMode
                ? 'text-yellow-300 hover:bg-yellow-900/30 hover:text-yellow-200'
                : 'text-gray-600 hover:bg-yellow-100 hover:text-gray-800'
            }`}
            aria-label="Dismiss banner"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DevelopmentBanner;
